import React, {useState, useEffect} from 'react';
import {Table} from 'react-bootstrap';
import Spinner from '@/components/ui/Spinner';
import axios from 'axios';
import BaseModal from './BaseModal';
import {useAlert} from '@/components/alerts';
import {buildURL} from '@/utils/urlBuilder';
import i18n from 'i18n';
import {extractErrorMessage} from '@/utils/errorHandling';

interface PermissionRole {
    id: number;
    name: string;
    user_count: number;
}

interface PermissionUser {
    id: number;
    username: string;
    email: string;
}

interface PermissionDetail {
    id: number;
    codename: string;
    name: string;
    content_type: string;
    domain: string;
    action: string;
    roles: PermissionRole[];
    users_with_direct_permission: PermissionUser[];
}

interface PermissionDetailModalProps {
    isOpen: boolean;
    onClose: () => void;
    permissionId: number | null;
}

const PermissionDetailModal: React.FC<PermissionDetailModalProps> = ({
    isOpen,
    onClose,
    permissionId
}) => {
    const [permission, setPermission] = useState<PermissionDetail | null>(null);
    const [loading, setLoading] = useState(false);
    const {showError} = useAlert();

    useEffect(() => {
        if (isOpen && permissionId) {
            fetchPermissionDetail();
        }
    }, [isOpen, permissionId]);

    const fetchPermissionDetail = async () => {
        if (!permissionId) {return;}
        
        setLoading(true);
        try {
            const response = await axios.get(buildURL('adminPermissionDetail', {permission_id: permissionId}));
            setPermission(response.data);
        } catch (error: any) {
            console.error('Failed to fetch permission details:', error);
            const errorMessage = extractErrorMessage(error, 'Failed to load permission details');
            showError(i18n.t('permission_detail_load_error.text', {error: errorMessage}));
        } finally {
            setLoading(false);
        }
    };

    const handleClose = () => {
        setPermission(null);
        onClose();
    };


    return (
        <BaseModal
            isOpen={isOpen}
            onClose={handleClose}
            title={i18n.t('permission_detail_title.text')}
            size="xl"
        >
            {loading ? (
                <div className="text-center py-4">
                    <Spinner 
                        variant="border" 
                        centered={true}
                        text={i18n.t('permission_detail_loading.text')}
                    />
                </div>
            ) : permission ? (
                <div>
                    <div className="mb-4">
                        <h5 className="mb-3">{permission.name}</h5>
                        <div className="row">
                            <div className="col-md-6">
                                <p><strong>{i18n.t('permission_detail_codename.text')}:</strong> <code>{permission.codename}</code></p>
                            </div>
                            <div className="col-md-6">
                                <p>
                                    <strong>{i18n.t('permissions_domain_header.text')}:</strong>
                                    {' '}{permission.domain}
                                </p>
                                <p>
                                    <strong>{i18n.t('permissions_action_header.text')}:</strong>
                                    {' '}{permission.action}
                                </p>
                            </div>
                        </div>
                    </div>

                    <div className="mb-4">
                        <h6>{i18n.t('permission_detail_assigned_roles.text', {count: permission.roles.length})}</h6>
                        {permission.roles.length > 0 ? (
                            <Table striped bordered hover size="sm">
                                <thead>
                                    <tr>
                                        <th>{i18n.t('permission_detail_role_name.text')}</th>
                                        <th>{i18n.t('permission_detail_users_in_role.text')}</th>
                                    </tr>
                                </thead>
                                <tbody>
                                    {permission.roles.map((role) => (
                                        <tr key={role.id}>
                                            <td>{role.name}</td>
                                            <td>
                                                {role.user_count} {role.user_count === 1 ? i18n.t('roles_user_singular.text') : i18n.t('roles_user_plural.text')}
                                            </td>
                                        </tr>
                                    ))}
                                </tbody>
                            </Table>
                        ) : (
                            <p className="text-muted">{i18n.t('permission_detail_no_roles.text')}</p>
                        )}
                    </div>

                    <div>
                        <h6>{i18n.t('permission_detail_direct_users.text', {count: permission.users_with_direct_permission.length})}</h6>
                        {permission.users_with_direct_permission.length > 0 ? (
                            <Table striped bordered hover size="sm">
                                <thead>
                                    <tr>
                                        <th>{i18n.t('role_detail_username_header.text')}</th>
                                        <th>{i18n.t('role_detail_email_header.text')}</th>
                                    </tr>
                                </thead>
                                <tbody>
                                    {permission.users_with_direct_permission.map((user) => (
                                        <tr key={user.id}>
                                            <td>{user.username}</td>
                                            <td>{user.email}</td>
                                        </tr>
                                    ))}
                                </tbody>
                            </Table>
                        ) : (
                            <p className="text-muted">{i18n.t('permission_detail_no_direct_users.text')}</p>
                        )}
                    </div>
                </div>
            ) : null}
        </BaseModal>
    );
};

export default PermissionDetailModal;
